import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, type ReadToastOptions, useFetch, useMutation } from '../api';

type GetFleet = ApiClient['v2']['workspaces'][':workspaceId']['fleets'][':fleetId']['$get'];
export type FleetResponse = InferResponseType<GetFleet, 200>;

export function useFleet(
  workspaceId: number | null,
  fleetId: number | null,
  toastOptions?: ReadToastOptions
) {
  return useFetch<FleetResponse>(
    workspaceId !== null && fleetId !== null ? `workspaces/${workspaceId}/fleets/${fleetId}` : null,
    (client) =>
      client.v2.workspaces[':workspaceId'].fleets[':fleetId'].$get({
        param: { workspaceId: workspaceId!.toString(), fleetId: fleetId!.toString() },
      }),
    toastOptions
  );
}

export function useUpdateFleet(workspaceId: number, fleetId: number) {
  type PatchFleet = ApiClient['v2']['workspaces'][':workspaceId']['fleets'][':fleetId']['$patch'];
  type PatchFleetRequest = InferRequestType<PatchFleet>['json'];
  type FleetResponse = InferResponseType<PatchFleet, 200>;

  return useMutation<FleetResponse, PatchFleetRequest>(
    `workspaces/${workspaceId}/fleets/${fleetId}`,
    (client, updates) =>
      client.v2.workspaces[':workspaceId'].fleets[':fleetId'].$patch({
        param: { workspaceId: workspaceId.toString(), fleetId: fleetId.toString() },
        json: updates,
      })
  );
}

export function useCreateFleet(
  workspaceId: number,
  actionOnErr: (error?: unknown) => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/fleets`;

  type CreateFleet = ApiClient['v2']['workspaces'][':workspaceId']['fleets']['$post'];
  type CreateFleetRequest = InferRequestType<CreateFleet>['json'];
  type FleetResponse = InferResponseType<CreateFleet, 201>;

  const createFleetMutation = useMutation<FleetResponse, CreateFleetRequest>(
    listCacheKey,
    (client, data) =>
      client.v2.workspaces[':workspaceId'].fleets.$post({
        param: { workspaceId: workspaceId.toString() },
        json: data,
      }),
    actionOnErr,
    { suppressToast: true }
  );

  return useCallback(
    async (data: CreateFleetRequest) => {
      const result = await createFleetMutation(data);
      await mutate(
        (key: unknown) =>
          typeof key === 'string' && (key === listCacheKey || key.startsWith(`${listCacheKey}?`))
      );
      return result;
    },
    [createFleetMutation, mutate, listCacheKey]
  );
}

export function useDeleteFleet(workspaceId: number, fleetId: number) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/fleets`;
  const itemCacheKey = `workspaces/${workspaceId}/fleets/${fleetId}`;

  const deleteFleetMutation = useMutation<undefined, void>(itemCacheKey, (client) =>
    client.v2.workspaces[':workspaceId'].fleets[':fleetId'].$delete({
      param: { workspaceId: workspaceId.toString(), fleetId: fleetId.toString() },
    })
  );

  return useCallback(async () => {
    await deleteFleetMutation();
    await mutate(
      (key: unknown) =>
        typeof key === 'string' && (key === listCacheKey || key.startsWith(`${listCacheKey}?`))
    );
    await mutate(itemCacheKey);
    return true;
  }, [deleteFleetMutation, mutate, listCacheKey, itemCacheKey]);
}
